import { useState, useEffect, useRef } from 'react';
import { Languages, Plus, Check } from 'lucide-react';
import { DeclarationInformation } from './DeclarationSummary/DeclarationInformation';
import { ItemInformation } from './DeclarationSummary/ItemInformation';
import { DocumentsTable } from './DeclarationSummary/DocumentsTable';
import { PackagesTable } from './DeclarationSummary/PackagesTable';
import { ValidationErrors } from './DeclarationSummary/ValidationErrors';
import { DeclarationDocumentsTable } from './DeclarationSummary/DeclarationDocumentsTable';
import { useDeclarationSummary } from './DeclarationSummary/useDeclarationSummary';
import { getDeclarationSummaryTranslation } from '../../hooks/useLanguage';
import { deepClone } from '../../utils/dataHelpers';
import { aggregateDocumentosFromPartidas, createPartidasDocumentosSignature } from '../../utils/declarationSummaryUtils';

export const DeclarationSummary = ({ jsonData, isSummaryExpanded, selectedOption }) => {
  const [activeDeclarationIndex, setActiveDeclarationIndex] = useState(-1); // -1 means "All"
  const [activeItemIndices, setActiveItemIndices] = useState({}); // Object mapping declarationIndex to itemIndex
  
  const {
    isSpanish,
    setIsSpanish,
    editableData,
    setEditableData,
    isDirty,
    setIsDirty,
    validationErrors,
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  } = useDeclarationSummary(jsonData);

  // Reset active declaration index if it's out of bounds (but keep -1 for "All")
  useEffect(() => {
    if (Array.isArray(editableData) && editableData.length > 0) {
      if (activeDeclarationIndex >= editableData.length && activeDeclarationIndex !== -1) {
        setActiveDeclarationIndex(-1); // Reset to "All" if out of bounds
      }
    }
  }, [editableData, activeDeclarationIndex]);

  // Helper function to get active item index for a declaration
  const getActiveItemIndex = (declarationIndex) => {
    return activeItemIndices[declarationIndex] ?? -1;
  };

  // Helper function to set active item index for a declaration
  const setActiveItemIndex = (declarationIndex, itemIndex) => {
    setActiveItemIndices(prev => ({
      ...prev,
      [declarationIndex]: itemIndex
    }));
  };

  // State for declaration-level documentos toggle (per declaration)
  const [hasDeclarationDocuments, setHasDeclarationDocuments] = useState({});
  const [removeDuplicates, setRemoveDuplicates] = useState({});
  const prevPartidasDocumentosRefs = useRef({});
  const isManualEditRefs = useRef({});
  const prevEditableDataLengthRef = useRef(editableData?.length || 0);

  // Note: aggregateDocumentosFromPartidas is now imported from utils

  // Handle toggle for declaration-level documentos
  const handleToggleDeclarationDocuments = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const updatedData = deepClone(editableData);
    const currentState = hasDeclarationDocuments[declarationIndex] || false;
    const newState = !currentState;
    
    if (newState) {
      // Enable: aggregate documentos from all partidas
      const removeDups = removeDuplicates[declarationIndex] || false;
      const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
      updatedData[declarationIndex].documentos = aggregated;
      // Update the ref signature to prevent immediate re-sync
      if (declaration.partidas) {
        prevPartidasDocumentosRefs.current[declarationIndex] = createPartidasDocumentosSignature(declaration);
      }
    } else {
      // Disable: clear declaration-level documentos
      updatedData[declarationIndex].documentos = [];
    }
    
    setEditableData(updatedData);
    setIsDirty(true);
    setHasDeclarationDocuments(prev => ({ ...prev, [declarationIndex]: newState }));
    isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
  };

  // Handle remove duplicates toggle
  const handleToggleRemoveDuplicates = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const currentState = removeDuplicates[declarationIndex] || false;
    const newState = !currentState;
    setRemoveDuplicates(prev => ({ ...prev, [declarationIndex]: newState }));
    
    if (hasDeclarationDocuments[declarationIndex]) {
      // Re-aggregate with new duplicate removal setting
      const updatedData = deepClone(editableData);
      const aggregated = aggregateDocumentosFromPartidas(declaration, newState);
      updatedData[declarationIndex].documentos = aggregated;
      setEditableData(updatedData);
      setIsDirty(true);
      isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
    }
  };

  // Sync declaration documentos when partidas change - optimized with signature comparison
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    const lengthChanged = editableData.length !== prevEditableDataLengthRef.current;
    prevEditableDataLengthRef.current = editableData.length;

    editableData.forEach((declaration, declarationIndex) => {
      if (!declaration) return;

      if (isManualEditRefs.current[declarationIndex]) {
        isManualEditRefs.current[declarationIndex] = false;
        return;
      }
      
      if (!hasDeclarationDocuments[declarationIndex] || !declaration.partidas) {
        return;
      }
      
      const currentSignature = createPartidasDocumentosSignature(declaration);
        const prevSignature = prevPartidasDocumentosRefs.current[declarationIndex];
      
      if (prevSignature === currentSignature && !lengthChanged) {
        return;
      }
      
          prevPartidasDocumentosRefs.current[declarationIndex] = currentSignature;
          const updatedData = deepClone(editableData);
          const removeDups = removeDuplicates[declarationIndex] || false;
          const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
          updatedData[declarationIndex].documentos = aggregated;
          setEditableData(updatedData);
          setIsDirty(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, hasDeclarationDocuments, removeDuplicates]);

  // Initialize toggle states based on declaration state
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    const newStates = {};
    const newRemoveDups = {};
    editableData.forEach((declaration, index) => {
      if (declaration) {
        const hasDocs = declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0;
        newStates[index] = hasDocs;
        // Initialize removeDuplicates to false if not set
        if (!(index in removeDuplicates)) {
          newRemoveDups[index] = false;
        } else {
          newRemoveDups[index] = removeDuplicates[index];
        }
      }
    });
    setHasDeclarationDocuments(prev => ({ ...prev, ...newStates }));
    setRemoveDuplicates(prev => ({ ...prev, ...newRemoveDups }));
  }, [editableData?.length]);

  // Reset active item indices when items change
  useEffect(() => {
    const newIndices = {};
    editableData.forEach((declaration, declarationIndex) => {
      const itemsCount = declaration.partidas?.length || 0;
      const currentIndex = activeItemIndices[declarationIndex] ?? -1;
      if (currentIndex >= itemsCount && currentIndex !== -1) {
        newIndices[declarationIndex] = -1; // Reset to "All" if out of bounds
      } else if (currentIndex !== -1) {
        newIndices[declarationIndex] = currentIndex; // Keep current if valid
      }
    });
    if (Object.keys(newIndices).length > 0) {
      setActiveItemIndices(prev => ({ ...prev, ...newIndices }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData]);

  return (
    <div className="declaration-summary">
      <div className="">
        <div className={`${selectedOption === "split" && "sticky"} top-0 bg-white pt-4 w-full z-999`}>
   
        </div>

        {isSummaryExpanded && (
          <div className="">

            {/* Show declarations based on active tab */}
            {Array.isArray(editableData) && (
              activeDeclarationIndex === -1 
                ? // Show all declarations
                  editableData.map((declaration, declarationIndex) => (
                    <div key={declarationIndex} className={`border-t pt-4 first:border-t-0 first:pt-0`}>
                      <h2 className={`text-4xl font-semibold mb-4 split-header uppercase ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation('export', isSpanish)} - {declaration.expediente || ''}
                      </h2>

                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {/* Auto-populate from partidas if available */}
                      <DeclarationDocumentsTable
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                        declarationIndex={declarationIndex}
                        isSpanish={isSpanish}
                        declaration={declaration}
                      />

                      {/* Items (Partidas) */}
                      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0 text-3xl font-semibold uppercase">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div className='split-table border-container p-4 rounded-md mt-4' key={itemIndex}>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                </div>
                              );
                        })()}
                      </div>
                    </div>
                  ))
                : // Show only active declaration
                  editableData[activeDeclarationIndex] && (() => {
                    const declaration = editableData[activeDeclarationIndex];
                    const declarationIndex = activeDeclarationIndex;
                    
                    return (
                    <div key={declarationIndex} className="mt-2 mb-2 border-t pt-4 first:border-t-0 first:pt-0">
                      <h2 className={`text-lg font-semibold mb-4 split-header ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation('export', isSpanish)} #{declarationIndex + 1}
                      </h2>

                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {/* Auto-populate from partidas if available */}
                      <DeclarationDocumentsTable
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                        declarationIndex={declarationIndex}
                        isSpanish={isSpanish}
                        declaration={declaration}
                      />

                      {/* Items (Partidas) */}
                      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0 text-3xl font-semibold uppercase">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="border-ta pt-4 mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div className='split-table border-container p-4 rounded-md mt-4' key={itemIndex}>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                </div>
                              );
                        })()}
                      </div>
                    </div>
                    );
                  })()
            )}
          </div>
        )}
      </div>
    </div>
  );
};